import { useState } from "react"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import type { GridColDef } from "@mui/x-data-grid-pro"
import { Link } from "react-router-dom"
import { DataGrid } from "@core/components/dataGrid/dataGrid"
import { RefreshTableButton } from "../common/refreshTableButton"
import type { TemplateWriteAccess } from "../../hooks/useVersionDraft"
import { usePostBotListState } from "../../hooks/usePostBotListState"
import { useRevisionConflict } from "../../hooks/useRevisionConflict"
import type { Placement } from "../../interface/schedule"
import {
  useGetPlacementsQuery,
  useLazyGetPlacementQuery,
  usePatchPlacementMutation,
} from "../../services/publications/placementsApi"
import { buildPlacementPatch } from "../../utils/patches"
import { placementPath } from "../../utils/placementDraft"
import { placementHistoryPath } from "../../utils/occurrences"
import { publicationPath } from "../../hooks/publicationSessionDraft"
import {
  TemplateQueryFeedback,
  useTemplateRowCount,
} from "../templates/templateQueryFeedback"
import { FeatureErrors } from "../common/featureErrors"
import { RevisionConflictDialog } from "../common/revisionConflictDialog"

const getRowHeight = () => "auto" as const

export interface PlacementsTableProps extends TemplateWriteAccess {
  channelId?: string
  publicationId?: string
  listKey?: string
}
export function PlacementsTable(props: PlacementsTableProps) {
  return (
    <PlacementsList
      key={`${props.channelId ?? ""}:${props.publicationId ?? ""}`}
      {...props}
    />
  )
}
function PlacementsList({
  channelId,
  publicationId,
  listKey,
  readOnly,
  canWrite,
}: PlacementsTableProps) {
  const list = usePostBotListState({
    resource: "placements",
    key:
      listKey ??
      `placements${channelId ? `-${channelId}` : ""}${publicationId ? `-${publicationId}` : ""}`,
  })
  const args = {
    ...list.listArgs,
    ...(channelId === undefined ? {} : { channelId }),
    ...(publicationId === undefined ? {} : { publicationId }),
  }
  const query = useGetPlacementsQuery(args)
  const page = !query.error ? query.currentData : undefined
  const rowCount = useTemplateRowCount(
    JSON.stringify({ ...args, page: undefined, limit: undefined }),
    page?.total,
  )
  const [target, setTarget] = useState<Placement>()
  const writable = !readOnly && canWrite !== false
  const columns = (
    [
      {
        field: "id",
        headerName: "Размещение",
        width: 170,
        renderCell: ({ row }) => (
          <Button component={Link} to={placementPath(row.id)}>
            Размещение {row.id}
          </Button>
        ),
      },
      {
        field: "publicationId",
        headerName: "Публикация",
        width: 220,
        renderCell: ({ row }) => (
          <Button
            component={Link}
            to={publicationPath(row.publicationId)}
            sx={{
              minWidth: 0,
              maxWidth: "100%",
              whiteSpace: "normal",
              overflowWrap: "anywhere",
              textAlign: "left",
            }}
          >
            {row.publication?.name?.trim() || row.publicationId}
          </Button>
        ),
      },
      {
        field: "channelId",
        headerName: "Канал",
        minWidth: 210,
        flex: 1,
        renderCell: ({ row }) => {
          const name =
            row.channel?.custom_name?.trim() ||
            row.channel?.title?.trim() ||
            row.channelId
          const comment = row.channel?.comment?.trim()
          return (
            <Stack
              sx={{
                py: 1,
                width: "100%",
                minWidth: 0,
                whiteSpace: "normal",
                overflowWrap: "anywhere",
              }}
            >
              <Typography variant="body2">{name}</Typography>
              {comment && (
                <Typography variant="caption" color="text.secondary">
                  {comment}
                </Typography>
              )}
            </Stack>
          )
        },
      },
      { field: "enabled", headerName: "Включено", type: "boolean", width: 100 },
      { field: "revision", headerName: "Ревизия", width: 100 },
      {
        field: "history",
        headerName: "История",
        width: 150,
        renderCell: ({ row }) => (
          <Button
            component={Link}
            to={placementHistoryPath(row.id)}
            aria-label={`История размещения ${row.id}`}
          >
            История отправок
          </Button>
        ),
      },
      {
        field: "actions",
        headerName: "Действия",
        width: 170,
        renderCell: ({ row }) => (
          <Button
            disabled={!writable || !!row.deletedAt || !!target}
            aria-label={`${row.enabled ? "Отключить" : "Включить"} ${row.id}`}
            onClick={() => setTarget(row)}
          >
            {row.enabled ? "Отключить" : "Включить"}
          </Button>
        ),
      },
    ] satisfies GridColDef<Placement>[]
  ).map((column) => ({ ...column, sortable: false, filterable: false }))
  return (
    <Stack spacing={2}>
      <Typography>
        Размещения — по убыванию ID. Отключение останавливает будущие отправки,
        не удаляя историю.
      </Typography>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {publicationId === undefined && (
          <TextField
            size="small"
            sx={{ width: { xs: "100%", sm: 280 }, maxWidth: "100%" }}
            label="ID публикации — фильтр"
            value={list.filters.publicationId ?? ""}
            onChange={(event) =>
              list.setFilters({
                publicationId: event.target.value || undefined,
              })
            }
          />
        )}
        {channelId === undefined && (
          <TextField
            size="small"
            sx={{ width: { xs: "100%", sm: 280 }, maxWidth: "100%" }}
            label="ID канала — фильтр"
            value={list.filters.channelId ?? ""}
            onChange={(event) =>
              list.setFilters({ channelId: event.target.value || undefined })
            }
          />
        )}
        <TextField
          size="small"
          sx={{ width: { xs: "100%", sm: 220 }, maxWidth: "100%" }}
          select
          label="Состояние отправок"
          value={
            list.filters.enabled === undefined
              ? "all"
              : String(list.filters.enabled)
          }
          onChange={(event) =>
            list.setFilters({
              enabled:
                event.target.value === "all"
                  ? undefined
                  : event.target.value === "true",
            })
          }
        >
          <MenuItem value="all">Все</MenuItem>
          <MenuItem value="true">Включены</MenuItem>
          <MenuItem value="false">Отключены</MenuItem>
        </TextField>
        <TextField
          size="small"
          sx={{ width: { xs: "100%", sm: 220 }, maxWidth: "100%" }}
          select
          label="Статус размещения"
          value={list.filters.status ?? "live"}
          onChange={(event) =>
            list.setFilters({
              status: event.target.value as "live" | "deleted" | "all",
            })
          }
        >
          <MenuItem value="live">Действующие</MenuItem>
          <MenuItem value="deleted">Удалённые (история)</MenuItem>
          <MenuItem value="all">Все</MenuItem>
        </TextField>
      </Stack>
      <TemplateQueryFeedback
        query={query}
        empty={page?.data.length === 0 && "Размещений нет"}
      />
      <RefreshTableButton
        label="Обновить размещения"
        disabled={query.isFetching}
        onClick={() => void query.refetch()}
      />
      <Box sx={{ width: "100%", minHeight: 200 }}>
        <DataGrid
          {...list.gridProps}
          rows={page?.data ?? []}
          columns={columns}
          rowCount={rowCount}
          loading={query.isFetching}
          autoHeight
          getRowHeight={getRowHeight}
          disableRowSelectionOnClick
        />
      </Box>
      {target && (
        <PlacementToggleDialog
          key={target.id}
          initial={target}
          writable={writable}
          onClose={() => setTarget(undefined)}
        />
      )}
    </Stack>
  )
}

/** Outside virtualized cells: refetch/error/pagination must not discard CAS intent. */
function PlacementToggleDialog({
  initial,
  writable,
  onClose,
}: {
  initial: Placement
  writable: boolean
  onClose: () => void
}) {
  const [patch] = usePatchPlacementMutation()
  const [fetchRemote] = useLazyGetPlacementQuery()
  const conflict = useRevisionConflict({
    resourceKey: `placement:${initial.id}`,
    initial,
    fields: ["enabled"],
    fetchRemote: () => fetchRemote(initial.id, false).unwrap(),
  })
  const [enabled, setEnabled] = useState(!initial.enabled)
  const [error, setError] = useState<unknown>()
  const [compare, setCompare] = useState(true)
  const { state } = conflict
  const busy = state.status === "saving"
  const save = async () => {
    if (!writable || busy || state.status === "conflict") return
    const body = buildPlacementPatch(state.baseline, { enabled })
    if (!body) return
    const attempt = conflict.beginSave({ ...state.draft, enabled })
    if (!attempt) return
    setError(undefined)
    try {
      const saved = await patch({ id: initial.id, body }).unwrap()
      attempt.succeed(saved)
      onClose()
    } catch (failure) {
      setError(failure)
      await attempt.fail(failure)
      setCompare(true)
    }
  }
  return (
    <>
      <Dialog open onClose={busy ? undefined : onClose}>
        <DialogTitle>
          {enabled ? "Включить" : "Отключить"} размещение {initial.id}?
        </DialogTitle>
        <DialogContent>
          <Typography>
            Изменится только это размещение. Публикация и другие размещения не
            изменятся.
          </Typography>
          <FeatureErrors error={error} />
          <FeatureErrors error={state.fetchError} />
          {state.status === "conflict" && (
            <Button onClick={() => setCompare(true)}>Сравнить изменения</Button>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={onClose}>
            Отмена
          </Button>
          <Button
            disabled={
              !writable ||
              busy ||
              state.status === "conflict" ||
              enabled === state.baseline.enabled
            }
            onClick={() => void save()}
          >
            Сохранить переключение {initial.id}
          </Button>
        </DialogActions>
      </Dialog>
      {state.status === "conflict" && (
        <RevisionConflictDialog
          open={compare}
          differences={state.differences}
          onClose={() => setCompare(false)}
          onApply={(choices) => {
            const resolved = conflict.resolve(choices)
            if (resolved) setEnabled(resolved.draft.enabled)
            setCompare(false)
            setError(undefined)
          }}
        />
      )}
    </>
  )
}
